import { BOQ_STATUS_LABEL, type BoqStatus } from "../../api/boq";
import { ORDER_STATUS_LABEL, type OrderStatus } from "../../api/purchaseOrders";
import { STATUS_LABEL, type QuotationStatus } from "../../api/quotations";
import type { Tone } from "../../components/ui";

/** Every document status as a coloured word, one palette across the chain.
 *
 * Grey is "not started or finished without effect", amber "waiting on
 * somebody", green "done, and it counted", red "refused or cancelled". Before
 * this the PO and BOQ statuses had no colours at all (Fix #5), so an issued
 * order and a cancelled one looked the same. */

const QUOTATION_TONE: Record<QuotationStatus, Tone> = {
  draft: "draft",
  submitted: "warn",
  under_review: "warn",
  resubmitted: "warn",
  returned: "warn",
  approved: "ok",
  rejected: "crit",
  superseded: "crit",
};

const ORDER_TONE: Record<OrderStatus, Tone> = {
  pending_generation: "quiet",
  draft: "draft",
  generated: "quiet",
  issued: "warn",
  sent: "info",
  acknowledged: "ok",
  cancelled: "crit",
};

const BOQ_TONE: Record<BoqStatus, Tone> = {
  pending_generation: "quiet",
  draft: "draft",
  generated: "quiet",
  submitted: "warn",
  approved: "ok",
  returned: "warn",
  rejected: "crit",
  superseded: "crit",
};

const CUSTOMER_ORDER_TONE: Record<string, Tone> = {
  awaiting: "quiet",
  received: "warn",
  validated: "ok",
  revised: "warn",
  closed: "ok",
  rejected: "crit",
  cancelled: "crit",
};

export function quotationTone(status: QuotationStatus): Tone {
  return QUOTATION_TONE[status] ?? "quiet";
}

export function orderTone(status: string): Tone {
  return ORDER_TONE[status as OrderStatus] ?? "quiet";
}

export function orderLabel(status: string): string {
  return ORDER_STATUS_LABEL[status as OrderStatus] ?? humanise(status);
}

export function boqTone(status: string): Tone {
  return BOQ_TONE[status as BoqStatus] ?? "quiet";
}

export function boqLabel(status: string): string {
  return BOQ_STATUS_LABEL[status as BoqStatus] ?? humanise(status);
}

const CUSTOMER_ORDER_LABEL: Record<string, string> = {
  received: "To check",
  revised: "Revised · to check",
  validated: "Validated",
  closed: "Closed",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export function customerOrderLabel(status: string): string {
  return CUSTOMER_ORDER_LABEL[status] ?? humanise(status);
}

export function customerOrderTone(status: string): Tone {
  return CUSTOMER_ORDER_TONE[status] ?? "quiet";
}

export function quotationLabel(status: QuotationStatus): string {
  return STATUS_LABEL[status] ?? humanise(status);
}

/** A status key the tables have no label for, said as words: "under_review"
 * reads "Under review" rather than showing the key (Fix #4). */
export function humanise(status: string): string {
  const words = status.replace(/_/g, " ").trim();
  return words === "" ? "—" : words[0].toUpperCase() + words.slice(1);
}
